import { useCallback, useState } from "react";
import ProfielBreadCrumbsV2 from "../ProfielBreadCrumbsV2";
import { useAppContext } from "@/context/AppContext";
import { useAuthContext } from "@/context/AuthContext";
import { useRouter } from "next/navigation";
import ExpandedHuiswerkModusSelect from "./ExpandedHuiswerkModusSelect";
import MobileDesktopTip from "./MobileDesktopTip";

export default function ModusSelectV2({
  hideTitle = false,
  hideBreadcrumbs = false
}: {
  hideTitle?: boolean;
  hideBreadcrumbs?: boolean;
}) {
  const { selectModus, isMobile } = useAppContext();
  const { userData } = useAuthContext();
  const router = useRouter();
  const [isHuiswerkExpanded, setIsHuiswerkExpanded] = useState(false);

  const savedSession = userData?.huiswerk_set;

  const startMode = useCallback(
    (modus: "uitleg" | "huiswerk") => {
      if (modus === "huiswerk" && isMobile) return;
      selectModus(modus, "mode_card");
      router.push("/library");
    },
    [isMobile, router, selectModus]
  );

  return (
    <div className="px-4 md:px-8">
      {!hideTitle && (
        <div className="mb-8 mt-4 text-center">
          <h1 className="mb-2 text-3xl font-bold text-gray-800">
            Aan de slag met WisWiz
          </h1>
        </div>
      )}

      {!hideBreadcrumbs && <ProfielBreadCrumbsV2 />}

      <p className="mt-6 text-center text-xl font-bold text-gray-900 md:text-2xl">
        Wat wil je doen?
      </p>

      <div className="mx-auto mt-6 grid w-full max-w-xl grid-cols-1 gap-3 md:hidden">
        <button
          type="button"
          onClick={() => startMode("uitleg")}
          className="group flex w-full items-center gap-3 rounded-xl border border-mathorange/70 bg-mathorange/[0.03] px-4 py-3 text-left transition-colors hover:border-mathorange hover:bg-mathorange/[0.06] focus:outline-none focus:ring-2 focus:ring-mathorange/25"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-mathorange/15 text-mathorange [&_svg]:h-[18px] [&_svg]:w-[18px]">
            <LightbulbIcon />
          </span>
          <span className="flex min-w-0 flex-col items-start">
            <span className="text-base font-bold leading-5 text-gray-800">
              Losse opgave
            </span>
            <span className="mt-0.5 text-xs leading-5 text-gray-500">
              Uitleg bij de opgave waar je vastloopt
            </span>
          </span>
        </button>

        <button
          type="button"
          disabled
          className="group flex w-full cursor-not-allowed items-center gap-3 rounded-xl border border-dashed border-huiswerk/60 bg-huiswerk/[0.03] px-4 py-3 text-left"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-huiswerk/15 text-huiswerk opacity-60 [&_svg]:h-[18px] [&_svg]:w-[18px]">
            <BookIcon />
          </span>
          <span className="flex min-w-0 flex-col items-start">
            <span className="text-base font-bold leading-5 text-gray-500">
              Huiswerkset
            </span>
            <span className="mt-0.5 inline-flex items-center gap-1 text-xs text-gray-400">
              <DesktopIcon />
              Alleen op tablet of computer
            </span>
          </span>
        </button>
      </div>

      <div className="mx-auto mt-6 hidden w-full max-w-xl grid-cols-1 gap-5 md:grid md:grid-cols-2">
        <button
          type="button"
          onClick={() => startMode("uitleg")}
          className="group flex aspect-[4/3] w-full flex-col items-center justify-center gap-4 rounded-2xl border-2 border-mathyellow bg-white px-5 py-6 text-center shadow-sm transition-colors hover:bg-mathyellow/10 focus:outline-none focus:ring-2 focus:ring-mathyellow/45"
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-mathorange/15 text-mathorange shadow-sm ring-1 ring-mathorange/20 transition-transform duration-200 group-hover:scale-105 [&_svg]:h-5 [&_svg]:w-5">
            <LightbulbIcon />
          </span>
          <span className="grid min-w-0 gap-2">
            <span className="text-xl font-semibold leading-6 text-gray-950">
              Losse opgave
            </span>
            <span className="mx-auto max-w-[12rem] text-[13px] font-normal leading-6 text-gray-500">
              Uitleg bij de opgave waar je vastloopt
            </span>
          </span>
        </button>

        <div className="flex min-h-0 w-full flex-col items-center">
          {!isHuiswerkExpanded && (
            <button
              type="button"
              onClick={() => {
                if (!savedSession) {
                  startMode("huiswerk");
                  return;
                }
                setIsHuiswerkExpanded(true);
              }}
              disabled={Boolean(isMobile)}
              className="group flex aspect-[4/3] w-full flex-col items-center justify-center gap-4 rounded-2xl border-2 border-teal-700 bg-white px-5 py-6 text-center shadow-sm transition-colors hover:bg-teal-50 focus:outline-none focus:ring-2 focus:ring-teal-700/25 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:shadow-none disabled:[&_.mode-button-icon]:bg-gray-100 disabled:[&_.mode-button-icon]:text-gray-400 disabled:[&_.mode-button-icon]:ring-gray-200 disabled:[&_.mode-button-label]:text-gray-400 disabled:[&_.mode-button-subtitle]:text-gray-400"
            >
              <span className="mode-button-icon flex h-10 w-10 items-center justify-center rounded-xl bg-teal-700/10 text-teal-700 shadow-sm ring-1 ring-teal-700/20 transition-transform duration-200 group-hover:scale-105 [&_svg]:h-5 [&_svg]:w-5">
                <BookIcon />
              </span>
              <span className="grid min-w-0 gap-2">
                <span className="mode-button-label text-xl font-semibold leading-6 text-gray-950">
                  Huiswerkset
                </span>
                <span className="mode-button-subtitle mx-auto max-w-[12rem] text-[13px] font-normal leading-6 text-gray-500">
                  WisWiz begeleidt je door jouw huiswerk
                </span>
              </span>
            </button>
          )}

          {savedSession && isHuiswerkExpanded && (
            <div className="group relative flex max-h-[23rem] min-h-0 w-full flex-col overflow-hidden rounded-2xl border-2 border-huiswerk bg-white shadow-xl">
              <ExpandedHuiswerkModusSelect
                setExpanded={setIsHuiswerkExpanded}
              />
            </div>
          )}
        </div>
      </div>

      {isMobile && <MobileDesktopTip />}
    </div>
  );
}

function LightbulbIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 1 1 7.072 0l-.548.547A3.374 3.374 0 0 0 14 18.469V19a2 2 0 1 1-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
      />
    </svg>
  );
}

function BookIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
      />
    </svg>
  );
}

function DesktopIcon() {
  return (
    <svg
      className="h-3 w-3 shrink-0"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2z"
      />
    </svg>
  );
}
